/* eslint-disable import/no-cycle */
import {useContext, useState} from 'react';
import * as moment from 'moment';
import {useTranslation} from 'react-i18next';
import {observer} from 'mobx-react-lite';
import {StoreContext} from '../../stores/rootStore';
import KeyValueTable from '../shared/tables/KeyValueTable';
import {detailedDatetimeFormat, detailedDurationFormat} from '../../config/moment';
import {formatDuration} from '../../utils/formatting';
import {useMount} from '../../hooks/useMount';
import {EnDash} from '../shared/unicodeWrapper/EnDash';
import appConfig from '../../utils/appConfig';

const LogDetails = ({log, nextLog}) => {
  const {t} = useTranslation();
  const store = useContext(StoreContext);

  const [momentStart, setMomentStart] = useState(moment(log.start || log.createdAt));
  const [momentEnd, setMomentEnd] = useState(moment(log.end || nextLog?.createdAt));
  const [duration, setDuration] = useState(moment.duration(momentEnd.diff(momentStart)));
  const [operation, setOperation] = useState(null);
  const [state, setState] = useState(null);
  const [createdByUser, setCreatedByUser] = useState(null);
  const [updatedByUser, setUpdatedByUser] = useState(null);
  const [interruptionReason, setInterruptionReason] = useState(null);

  useMount(() => {
    setMomentStart(moment(log.start || log.createdAt));
  }, [log.start, log.createdAt]);

  useMount(() => {
    setMomentEnd(moment(log.end || nextLog?.createdAt));
  }, [log.end, nextLog?.createdAt]);

  useMount(() => {
    setDuration(moment.duration(momentEnd.diff(momentStart)));
  }, [momentStart, momentEnd]);

  useMount(() => {
    if (log.operationId) {
      setOperation(store.operationStore.getById(log.operationId));
      setState(store.operationStateStore.getById(log.stateId));
    } else {
      setOperation(null);
      setState(store.workplaceStateStore.getById(log.stateId));
    }
  }, [log.stateId, log.operationId]);

  useMount(() => {
    setCreatedByUser(store.userStore.getById(log.createdBy));
  }, [log.createdBy]);

  useMount(() => {
    setUpdatedByUser(store.userStore.getById(log.updatedBy));
  }, [log.updatedBy]);

  useMount(() => {
    setInterruptionReason(store.interruptionReasonStore.getById(log.interruptionReasonId));
  }, [log.interruptionReasonId]);

  const rows = [[t('state.historyModal.details.element'), state?.name || t('state.historyModal.personnel')]];
  if (state?.isInterruption) {
    rows.push(
      [t('state.historyModal.details.interruptionReasonSource'), interruptionReason?.reasonSource?.name || EnDash()],
      [t('state.historyModal.details.interruptionReason'), interruptionReason?.name || EnDash()]
    );
    Object.keys(appConfig.interruptionTags).forEach((key) => {
      rows.push([appConfig.interruptionTags[key], (log.tags && log.tags[key]) || EnDash()]);
    });
    rows.push([t('state.historyModal.details.interruptionMessage'), log?.message || EnDash()]);
  }
  if (operation) {
    rows.push(
      [t('state.historyModal.details.orderNo'), operation?.order?.no],
      [t('state.historyModal.details.materialNo'), operation?.order?.material?.no],
      [t('state.historyModal.details.materialname'), operation?.order?.material?.name]
    );
  }
  rows.push([t('state.historyModal.details.creator'), createdByUser?.fullName]);
  if (state?.isInterruption) {
    rows.push([t('state.historyModal.details.editor'), log.end ? updatedByUser?.fullName : EnDash()]);
  }
  rows.push(
    [t('state.historyModal.details.start'), momentStart.format(detailedDatetimeFormat)],
    [
      t('state.historyModal.details.end'),
      log.end || nextLog?.createdAt ? momentEnd.format(detailedDatetimeFormat) : EnDash(),
    ],
    [t('state.historyModal.details.duration'), formatDuration(duration, detailedDurationFormat)]
  );
  if (log.personnel !== undefined && log.personnel !== null) {
    rows.push(
      [t('state.historyModal.details.actual'), log.personnel]
    );
  }

  return (
    <KeyValueTable rows={rows}/>
  );
};

export default observer(LogDetails);
